import {Expressions, matchExpression, Variable} from '@packages/systems/wfdl';
import {ECall, EList, type Expression} from '@packages/systems/wfdl-ast';
import {
  identifier,
  namesEqual,
  pluginName,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import {constantFalse} from '@packages/utilities/fp/utils';

// ==========================================================================
// Append expression utilities
// ==========================================================================
const appendName = qualify(pluginName('List'), identifier('Append'));

/**
 * Returns true if the expression is a reference to the `List.Append` variable.
 */
export const isAppendVariable = matchExpression({
  Variable: Variable.match({
    qualified: (name) => namesEqual(name, appendName),
    unqualified: constantFalse,
  }),
  _: constantFalse,
});

/**
 * Strips one annotation layer if the expression is annotated.
 * At runtime, ContextualExpression is [ExpressionF<CE>, annotation] (tuple).
 * Expression is ExpressionF<Expression> (plain object).
 */
const unwrap = <T>(expr: T): Expression =>
  (Array.isArray(expr) ? expr[0] : expr) as unknown as Expression;

const getCall = <T>(expr: T): {fun: T; arg: T} | null =>
  Expressions.getCall(unwrap(expr)) as {fun: T; arg: T} | null;

/**
 * Recursively flattens nested `List.Append` calls into an array of expressions.
 * Works with both Expression and ContextualExpression via runtime introspection.
 */
export const deconstructAppend = <T>(expr: T): T[] => {
  const outerCall = getCall(expr);
  if (!outerCall) return [expr];
  const innerCall = getCall(outerCall.fun);
  if (!innerCall || !isAppendVariable(unwrap(innerCall.fun))) return [expr];
  return [innerCall.arg, ...deconstructAppend(outerCall.arg)];
};

const appendCall = (acc: Expression, list: Expression): Expression =>
  ECall({
    fun: ECall({
      fun: Expressions.qualifiedVariable('List', 'Append'),
      arg: list,
    }),
    arg: acc,
  });

/**
 * Builds nested `Append` calls from an array of list expressions.
 */
export const constructAppend = (lists: Expression[]): Expression => {
  if (lists.length < 2) return lists[0] ?? EList([]);
  return lists
    .slice(0, -1)
    .reduceRight<Expression>(appendCall, lists[lists.length - 1]!);
};
